import {
  Component,
  ElementRef,
  forwardRef,
  Input,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  FormsModule,
} from '@angular/forms';
import { IonInput, IonicModule } from '@ionic/angular';
import { numberIsValidNumberFormat } from 'src/app/helpers/ui-helpers';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-currency',
  templateUrl: './currency.component.html',
  styleUrls: ['./currency.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CurrencyComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [IonicModule, FormsModule, NgClass],
})
export class CurrencyComponent implements ControlValueAccessor {
  protected amount = '';
  protected hasErrors: boolean = false;

  //  Accessibility Props

  @Input() ariaLabel: string = '';
  @Input() set 'aria-label'(value: string) {
    this.ariaLabel = value;
  }
  @Input() set 'attr.aria-label'(value: string) {
    this.ariaLabel = value;
  }

  @Input() ariaLabelledBy: string = '';
  @Input() set 'aria-labelledBy'(value: string) {
    this.ariaLabelledBy = value;
  }
  @Input() set 'attr.aria-labelledBy'(value: string) {
    this.ariaLabelledBy = value;
  }

  @Input() ariaDescribedBy: string = '';
  @Input() set 'aria-describedBy'(value: string) {
    this.ariaDescribedBy = value;
  }
  @Input() set 'attr.aria-describedBy'(value: string) {
    this.ariaDescribedBy = value;
  }

  @Input() id: string = '';

  //  Input Props

  @Input() name: string = '';
  @Input() placeholder: string = '';
  //  Validation
  @Input() required: boolean = false;
  @Input() set value(val: string | number) {
    this.writeValue(val);
  }

  //  Ion Input Props
  /** Ion input color property, defaulted to 'primary' */
  @Input() color:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'success'
    | 'warning'
    | 'danger'
    | 'light'
    | 'medium'
    | 'dark' = null;

  @Input() disabled: boolean = false;

  @Input() prefix: string = '';

  @ViewChild('currency') currencyRef: ElementRef<IonInput>;

  handleChange(newValue: string | number | undefined | null) {
    this.onTouched();
    const isValidNumberFormat = numberIsValidNumberFormat(newValue?.toString().trim());
    const value = isValidNumberFormat
      ? newValue?.toString().trim()?.replaceAll(',', '')
      : newValue.toString();
    if (value === undefined || value === null || value === '') {
      return this.onChanged(null);
    }
    const numValue = Number(newValue);
    if (isNaN(numValue) || value.includes(',')) {
      return this.onChanged(value);
    }
    return this.onChanged(numValue);
  }

  onChanged: Function = (value: number) => {};
  /**
   * Registers a callback function that is called when the control's value changes in the UI.
   * This method is called by the forms API on initialization to update the form model when values propagate from the view to the model.
   */
  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  handleBlur() {
    //  mark as touched
    this.onTouched();
    //  if a number, format on blur
    const numValue = Number(this.amount);
    //  Number(null) = 0; Number(undefined) = NaN
    if (!isNaN(numValue) && this.amount !== '' && !this.hasErrors)
      this.amount = this.format(numValue);
  }

  onTouched: Function = () => {};

  /** Registers a callback function that is called by the forms API on initialization to update the form model on blur.*/
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Writes a new value to the element.
   * This method is called by the forms API to write to the view when programmatic changes from model to view are requested.
   */
  writeValue(value: string | number): void {
    this.amount = this.format(value);
  }

  /** Function that is called by the forms API when the control status changes to or from 'DISABLED'. Depending on the status, it enables or disables the appropriate DOM element. */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate = (control: AbstractControl<number | string>): ValidationErrors | null => {
    this.hasErrors = false;
    //  no value
    if (control.value === null || control.value === undefined || control.value === '') return null;

    //  not a valid number
    if (typeof control.value !== 'number') {
      this.hasErrors = true;
      return { number: control.value };
    }

    //  is a valid number
    //  zero decimals
    if (Math.floor(control.value.valueOf()) === control.value.valueOf()) return null;
    //  actually figure out the decimal length
    const decimalLength = control.value.toString().split('.')[1].length || 0;
    if (decimalLength > 2) {
      this.hasErrors = true;
      return { decimal: 2 };
    }

    // all good, no errors
    return null;
  };

  private format(value: string | number) {
    let _value = value;
    //  typeof undefined === 'undefined'; typeof null === 'object'
    if (typeof _value === 'number') {
      _value = Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
        .format(_value)
        .replace('$', '');
    }
    return _value;
  }
}
